import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Mail, Lock, Eye, EyeOff } from 'lucide-react';

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/tiwistock-logo.png"
            alt="TiwiStock"
            className="w-32 h-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold font-display">Welcome Back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your TiwiStock account</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4" data-testid="login-form">
          {error && (
            <div className={`rounded-lg p-3 text-sm ${
              error.includes('pending') 
                ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`} data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 bg-card-hover border border-border rounded-lg text-sm"
                placeholder="you@example.com"
                required
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-card-hover border border-border rounded-lg text-sm"
                placeholder="Your password"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Signing in...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" /> Sign In
              </span>
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-green-400 hover:text-green-300 font-medium">
              Create one
            </Link>
          </p>
          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-green-300 transition-colors">
              Forgot your password?
            </Link>
          </p>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-4">
          For support, contact <a href="mailto:contact@tiwisoft.com" className="text-green-400 hover:text-green-300">contact@tiwisoft.com</a>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
